import { useBoolean } from "ahooks";
import { useSnackbar } from "notistack";
import { Fragment, memo, useCallback, useMemo } from "react";
import { MediaDataRoomAudienceShareDialog } from "features/mediaDataRoom/components";
import {
  useAdvertiserAudiences,
  useMediaDataRoom,
} from "features/mediaDataRoom/contexts";
import {
  type AudienceV3,
  type MediaDataRoomParticipant,
  MediaDataRoomVersion,
} from "features/mediaDataRoom/models";

interface ShareAudienceButtonProps {
  audience: AudienceV3;
  renderTrigger: (open: () => void) => React.ReactNode;
}

const ShareAudienceButton = memo(
  ({ renderTrigger, audience }: ShareAudienceButtonProps) => {
    const [
      isShareAudienceDialogOpen,
      { setFalse: closeShareAudienceDialog, setTrue: openShareAudienceDialog },
    ] = useBoolean(false);
    const { enqueueSnackbar } = useSnackbar();
    const { shareAudience, isSharingAudience } = useAdvertiserAudiences(
      MediaDataRoomVersion.v3
    );
    const { participants } = useMediaDataRoom(MediaDataRoomVersion.v3);
    const availableParticipants = useMemo(
      () => participants.filter(({ isCurrentUser }) => !isCurrentUser),
      [participants]
    );
    const defaultSelectedParticipantIds = useMemo(
      () => audience.sharedWith.map(({ id }) => id),
      [audience.sharedWith]
    );
    const handleShareAudience = useCallback(
      async (selectedParticipants: MediaDataRoomParticipant[]) => {
        const sharedAudience = await shareAudience(
          audience.id,
          selectedParticipants
        );
        if (!sharedAudience) {
          return;
        }
        enqueueSnackbar(
          selectedParticipants.length && !audience.sharedWith.length
            ? `The Media DCR has been successfully shared with ${selectedParticipants.map(({ name }) => name).join(", ")}`
            : "Sharing settings have been successfully updated",
          { variant: "success" }
        );
        closeShareAudienceDialog();
      },
      [
        audience.id,
        closeShareAudienceDialog,
        enqueueSnackbar,
        shareAudience,
        audience.sharedWith.length,
      ]
    );
    return (
      <Fragment>
        {renderTrigger(openShareAudienceDialog)}
        <MediaDataRoomAudienceShareDialog
          availableParticipants={availableParticipants}
          defaultSelected={defaultSelectedParticipantIds}
          isLoading={isSharingAudience}
          onCancel={closeShareAudienceDialog}
          onShare={handleShareAudience}
          open={isShareAudienceDialogOpen}
        />
      </Fragment>
    );
  }
);

ShareAudienceButton.displayName = "ShareAudienceButton";

export default ShareAudienceButton;
